import React from "react";
import { useTranslation } from "react-i18next";

export default function GlassTable({
  rows = [
    { date: "2025-09-09", change: -1.2, price: 124.55 },
    { date: "2025-09-10", change: -0.8, price: 123.77 },
    { date: "2025-09-11", change: -2.1, price: 121.66 },
    { date: "2025-09-12", change: -0.4, price: 121.18 },
    { date: "2025-09-13", change: -1.0, price: 120.00 },
  ],
  title,
  currency,
  unit,
}) {
  const { t } = useTranslation();
  const displayTitle = title ?? t("charts.glassTable.title");
  return (
    <div style={{ background: "transparent" }}>
      {displayTitle ? (
        <div style={{ color: "#ffffff", fontWeight: 600, marginBottom: 12 }}>{displayTitle}</div>
      ) : null}
      <div style={{ overflowX: "auto" }}>
        <table className="glass-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>{t("charts.glassTable.date")}</th>
              <th>{t("charts.glassTable.change")}</th>
              <th>{t("charts.glassTable.price")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((r, idx, arr) => {
              const displayChange = resolveChange(arr, idx);
              return (
                <tr key={idx}>
                  <td>{r.date}</td>
                  <td className={displayChange < 0 ? "change-negative" : "change-positive"}>
                    {formatChange(displayChange)}
                  </td>
                  <td>{formatPrice(r.price, currency, unit)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 12px",
  borderBottom: "1px solid rgba(255,255,255,0.12)",
};

const tdStyle = {
  color: "#ffffff",
  padding: "10px 12px",
  fontSize: 13,
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const trStyle = {
  transition: "background 0.2s ease",
};

function formatPrice(v, currency, unit) {
  const num = typeof v === "number" ? v : parseFloat(v);
  const currencyPart = normalizeToken(currency);
  const unitPart = normalizeToken(unit);
  const suffix = currencyPart && unitPart
    ? ` ${currencyPart} / ${unitPart}`
    : currencyPart
      ? ` ${currencyPart}`
      : unitPart
        ? ` ${unitPart}`
        : '';

  if (Number.isFinite(num)) return `${num.toFixed(2)}${suffix}`;
  return String(v);
}

function normalizeToken(value) {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  if (['null', 'none', 'undefined'].includes(normalized.toLowerCase())) return '';
  return normalized;
}

function formatChange(v) {
  if (v === null || typeof v === "undefined") return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) {
    const sign = v > 0 ? "+" : ""; // always show sign for positive
    return `${sign}${v.toFixed(2)}%`;
  }
  return String(v);
}

function isNumber(val) {
  return typeof val === "number" && Number.isFinite(val);
}

function resolveChange(arr, idx) {
  const current = arr[idx];
  if (!current) return null;
  // Use provided change when it is a meaningful non-zero number
  if (isNumber(current.change) && current.change !== 0) return current.change;

  // Fallback: compute change from previous price if available
  if (idx > 0) {
    const prev = arr[idx - 1];
    if (isNumber(prev?.price) && isNumber(current.price) && prev.price !== 0) {
      const diffPct = ((current.price - prev.price) / prev.price) * 100;
      return diffPct;
    }
  }

  // If change is zero but explicitly provided, keep it
  if (isNumber(current.change)) return current.change;
  return null;
}
